import {readArticleSelection} from "./readArticleSelection";
import type {ReadingContext, ReadingSelection} from "../../types";
import {readingContextForDocument} from "./readingContext";
import {useEffect, useRef, useState} from "react";
import {Skeleton, Stack} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {dibAppApi} from "../../dibAppApi/dibAppApi";
import {useReportAccess} from "./AccessContext";
import {ResourceError} from "./ResourceError";
import {StatusMessage} from "./StatusMessage";
import {renderNode} from "./renderNode";
import type {DibDocument} from "./types";
import {TableOfContents} from "./TableOfContents";
import {CompactToc} from "./CompactToc";
import {RelatedContent} from "./RelatedContent";
import {SupplierInfo} from "./SupplierInfo";
import {useContentTitle} from "./useContentTitle";
import {useTocScrollSync} from "./useTocScrollSync";
import layoutClasses from "./ContentLayout.module.css";
import articleClasses from "./HtmlArticle.module.css";
import contentBaseClasses from "./contentBase.module.css";

const DocumentSkeleton = () => (
    <Stack gap="sm" pt="xl">
        <Skeleton height={32} width="60%" mb="md" />
        <Skeleton height={14} width="100%" />
        <Skeleton height={14} width="95%" />
        <Skeleton height={14} width="88%" />
        <Skeleton height={14} width="92%" />
        <Skeleton height={14} width="40%" />
    </Stack>
);

const renderDescription = (doc: DibDocument) =>
    doc.descroot.flatMap((entry) => {
        const el = doc.elements[entry.id];
        if (!el?.children) return [];
        return el.children
            .filter((c): c is {id: string} => "id" in c)
            .map((c) => renderNode(doc, c.id, {}, c.id));
    });

const renderContent = (doc: DibDocument) =>
    doc.root.map((entry) => renderNode(doc, entry.id, {}, entry.id));

type SegmentMove = {propsKey: string; segmentId: string};

export const HTMLResource = ({
    resourceId,
    segmentId,
    embedded,
    onReadingContextChange,
    onTextSelection,
}: {
    resourceId: string;
    segmentId: string | undefined;
    /** Shown inside another page (the chat work island). The host owns the
     *  URL, the tab title and the title row. */
    embedded?: {compactToc: boolean; sectionId?: string};
    onReadingContextChange?: (context: ReadingContext | null) => void;
    onTextSelection?: (selection: ReadingSelection) => void;
}) => {
    const {t} = useTranslation();

    // Embedded, the view moves between segments itself: the host's URL is
    // not its to change. A move is tagged with the props it was made under,
    // so it lapses as soon as the host sends new props (no reset effect).
    const propsKey = `${resourceId}|${segmentId ?? ""}|${embedded?.sectionId ?? ""}`;
    const [segmentMove, setSegmentMove] = useState<SegmentMove | null>(null);
    const movedTo =
        embedded && segmentMove?.propsKey === propsKey
            ? segmentMove.segmentId
            : undefined;
    const activeSegmentId = movedTo ?? segmentId;

    const {
        currentData: doc,
        isFetching,
        isError,
        error,
    } = dibAppApi.endpoints.getDocument.useQuery({
        resourceId,
        segmentId: activeSegmentId,
    });

    useReportAccess(resourceId, doc?.access);

    const {articleRef, tocNavRef, activeId, handleTocClick} = useTocScrollSync(
        doc,
        resourceId,
        activeSegmentId,
        // A moved-to segment lands on its own section, like the page's
        // segment links (…/segment/<id>#<id>).
        embedded ? {sectionId: movedTo ?? embedded.sectionId} : undefined
    );

    useEffect(() => {
        onReadingContextChange?.(isError ? null : readingContextForDocument(doc, resourceId, activeSegmentId, activeId));
    }, [doc, resourceId, activeSegmentId, activeId, isError, onReadingContextChange]);

    useEffect(() => () => onReadingContextChange?.(null), [onReadingContextChange]);

    useEffect(() => {
        const article = articleRef.current;
        if (!article || !doc?.access || !onTextSelection) return;
        const capture = () => {
            const quote = readArticleSelection(article, document.getSelection(), doc, activeSegmentId);
            // Collapsing the browser selection to type in chat must keep the attached quote.
            if (quote) onTextSelection(quote);
        };
        article.addEventListener("pointerup", capture);
        article.addEventListener("keyup", capture);
        return () => {
            article.removeEventListener("pointerup", capture);
            article.removeEventListener("keyup", capture);
        };
    }, [doc, activeSegmentId, onTextSelection, articleRef]);

    // --- Segment continuity (issue #169) ---
    // currentData (doc) goes undefined the instant the segmentId query arg
    // changes to an uncached key, which is what blanks the TOC and info column
    // today. Retain the last loaded document and keep rendering the chrome from
    // it while a SAME-topic segment fetch is in flight.
    const prevDocRef = useRef<DibDocument | null>(null);
    if (doc) prevDocRef.current = doc;

    // Title from the retained doc so same-topic segment fetches (which blank
    // currentData) don't flicker the title back to "DIB". Embedded, the tab
    // title is the host's.
    useContentTitle((doc ?? prevDocRef.current)?.name, !embedded);

    // True only while fetching a new segment of the same topic. topicId is
    // stable across a top-level TOC click (on the page the <Link> only
    // changes segmentId; embedded, the click moves the view's segment), so a
    // different-topic navigation, a cold load, or an errored fetch all leave
    // this false and fall through to the full skeleton.
    const isIntraTopicFetch =
        !doc && isFetching && prevDocRef.current?.topicId === resourceId;

    // Drives the persistent chrome (TOC + info column). Live doc when present,
    // else the retained doc during an intra-topic fetch. The article keeps using
    // `doc` directly so it still shows the skeleton while the segment loads.
    const chromeDoc = doc ?? (isIntraTopicFetch ? prevDocRef.current : null);

    // Embedded segment entries: the current segment's entry scrolls like an
    // in-page entry, any other moves the view to that segment.
    const handleSegmentClick = (id: string) => {
        if (id === activeSegmentId) handleTocClick(id);
        else {
            onReadingContextChange?.(null);
            setSegmentMove({propsKey, segmentId: id});
        }
    };

    const toc = chromeDoc && (
        <TableOfContents
            document={chromeDoc}
            activeId={activeId}
            onTocClick={handleTocClick}
            onSegmentClick={embedded ? handleSegmentClick : undefined}
        />
    );

    return (
        <div
            className={
                embedded
                    ? `${layoutClasses.page} ${layoutClasses.embedded}`
                    : layoutClasses.page
            }
        >
            {/* A narrow host has no room for the TOC beside the article: a
                tick rail stands in for it and opens the TOC as a flyout.
                Keep this slot a conditional child, never an early return or
                a wrapper around the article: the scroll-sync hook binds its
                input listeners to the article element once, so it must not
                remount. */}
            {embedded?.compactToc ? (
                chromeDoc && (
                    <CompactToc
                        document={chromeDoc}
                        activeId={activeId}
                        navRef={tocNavRef}
                    >
                        {toc}
                    </CompactToc>
                )
            ) : (
                <nav
                    ref={tocNavRef}
                    className={layoutClasses.toc}
                    aria-label={t("Title.LabelOfContents", {defaultValue: "Table of contents"})}
                >
                    {toc}
                </nav>
            )}

            <article
                ref={articleRef}
                className={`${layoutClasses.article} ${contentBaseClasses.contentBase}`}
            >
                {isFetching && !doc && <DocumentSkeleton />}
                {isError && <ResourceError error={error} />}
                {doc && doc.root.length === 0 && (
                    <StatusMessage
                        title="Empty document"
                        detail="This document has no content to display."
                    />
                )}
                {doc && doc.root.length > 0 && (
                    <>
                        {/* The host shows the title. */}
                        {!embedded && (
                            <h1 className={articleClasses.title}>
                                {doc.name}
                            </h1>
                        )}
                        {/* Always rendered — the shared .desccontainer
                            treatment (contentBase.module.css) keeps the
                            divider under the title even without a
                            description. */}
                        <div className="desccontainer">
                            {renderDescription(doc)}
                        </div>
                        <div className={articleClasses.content}>
                            {renderContent(doc)}
                        </div>
                    </>
                )}
            </article>

            {/* Embedded, there is no info column. */}
            {!embedded && (
                <aside className={layoutClasses.related}>
                    {chromeDoc && (
                        <>
                            <SupplierInfo document={chromeDoc} />
                            <RelatedContent document={chromeDoc} />
                        </>
                    )}
                </aside>
            )}
        </div>
    );
};
